import type { Cell, GameState, Role } from '@xox/shared'
import { AnimatePresence, motion } from 'motion/react'
import { type KeyboardEvent, useRef, useState } from 'react'
import { Mark } from './Mark'

interface Props {
  game: GameState
  role: Role
  canPlay: boolean
  pendingCell: number | null
  vanishing: Cell | null
  /** Empty cells the rules forbid right now (the human-first house rule); drawn hatched. */
  blockedCells?: readonly number[]
  onMove: (cell: number) => void
}

export function Board({
  game,
  role,
  canPlay,
  pendingCell,
  vanishing,
  blockedCells = [],
  onMove,
}: Props) {
  const [focusIdx, setFocusIdx] = useState(4)
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const winSet = new Set<number>(game.winLine ?? [])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = {
      ArrowUp: -3,
      ArrowDown: 3,
      ArrowLeft: -1,
      ArrowRight: 1,
    }
    const delta = moves[e.key]
    if (delta === undefined) return
    e.preventDefault()
    const next = (focusIdx + delta + 9) % 9
    setFocusIdx(next)
    refs.current[next]?.focus()
  }

  const handleMove = (cell: number) => {
    if (!canPlay || game.board[cell] !== null || blockedCells.includes(cell)) return
    onMove(cell)
  }

  const statusText =
    game.status === 'playing'
      ? role === 'spectator'
        ? `${game.turn} to move`
        : game.turn === role
          ? 'Your turn'
          : `${game.turn} is thinking`
      : `${game.winner} wins`

  return (
    <div
      role="grid"
      aria-label={`Tic-tac-toe board. ${statusText}`}
      onKeyDown={onKeyDown}
      className="grid aspect-square grid-cols-3 grid-rows-3 gap-2 rounded-3xl bg-slate-200/70 p-2 dark:bg-slate-800/70 sm:gap-3 sm:p-3"
      style={{ width: 'min(92vw, 60dvh, 520px)' }}
    >
      {game.board.map((mark, i) => {
        const row = Math.floor(i / 3) + 1
        const col = (i % 3) + 1
        const isVanishing = vanishing === i
        const isWin = winSet.has(i)
        const isPending = pendingCell === i
        const isBlocked = mark === null && blockedCells.includes(i)
        const playable = canPlay && mark === null && !isBlocked
        const label = `Row ${row}, column ${col}: ${mark ?? (isBlocked ? 'not allowed for the first mark' : 'empty')}${isVanishing ? ', vanishes next' : ''}`
        return (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: board cells are positional by definition
            key={i}
            type="button"
            role="gridcell"
            ref={(el) => {
              refs.current[i] = el
            }}
            tabIndex={i === focusIdx ? 0 : -1}
            aria-label={label}
            aria-disabled={!playable}
            onFocus={() => setFocusIdx(i)}
            onClick={() => handleMove(i)}
            className={[
              'relative flex items-center justify-center rounded-2xl bg-white shadow-sm transition-transform dark:bg-slate-900',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
              playable
                ? 'cursor-pointer active:scale-95 hover:bg-slate-50 dark:hover:bg-slate-800'
                : 'cursor-default',
              isBlocked && canPlay
                ? 'cursor-not-allowed bg-[repeating-linear-gradient(135deg,transparent_0_8px,var(--color-slate-200)_8px_10px)] dark:bg-[repeating-linear-gradient(135deg,transparent_0_8px,var(--color-slate-700)_8px_10px)]'
                : '',
              isWin ? 'ring-4 ring-emerald-400 dark:ring-emerald-500' : '',
              isPending ? 'scale-95 bg-slate-100 dark:bg-slate-800' : '',
            ].join(' ')}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {mark && (
                <motion.div
                  // biome-ignore lint/suspicious/noArrayIndexKey: identity is (mark, position) by design
                  key={`${mark}-${i}`}
                  className={['size-[62%]', isVanishing ? 'animate-pulse-slow' : ''].join(' ')}
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: isVanishing ? 0.45 : 1 }}
                  exit={{ scale: 0.4, opacity: 0, filter: 'blur(4px)' }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                  <Mark mark={mark} />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        )
      })}
    </div>
  )
}
